import type { CopilotProposalResourceType } from "@repo/app-types/entities/copilotProposal";
import { ButtonVariant } from "@repo/ui/Buttons/button";
import { IconButton } from "@repo/ui/Buttons/iconButton";
import { Size } from "@repo/ui/helpers/helpers";
import {
  type Icon,
  IconArrowRight,
  IconChevronDown,
  IconEye,
  IconFileText,
} from "@tabler/icons-react";
import { useState } from "react";
import { twMerge } from "tailwind-merge";
import { resourceTypeIcons } from "../../app/navigation/resourceTypeIcons.js";

export interface CopilotDeepLinkListRow<Id extends string = string> {
  /** The row's id, which is also what opening the row navigates to. */
  id: Id;
  /** What kind of resource the row configures; titles the list. */
  resourceType: CopilotProposalResourceType;
  name: string;
  /**
   * Where a proposal ended up. Only a pending row opens — the rest are the
   * record of what became of it: your Apply / Discard decision, or `stale`
   * because a different proposal on the same node was applied. A row with no
   * lifecycle (a test result) leaves it unset and always opens.
   */
  status?: "pending" | "applied" | "discarded" | "stale";
  /**
   * What the row targets, shown under the name. Set only where the list
   * spans more than one, since naming the same playbook on every row is noise.
   */
  resourceName?: string;
}

export interface CopilotDeepLinkListProps<Id extends string = string> {
  rows: readonly CopilotDeepLinkListRow<Id>[];
  onSelect: (row: CopilotDeepLinkListRow<Id>) => void;
  /**
   * The row the open editor is showing. It reads as Viewing and does not
   * open; the other rows still open, which is how the list moves between
   * nodes.
   */
  activeRowId?: Id;
  /** Overrides the title derived from the rows' resource types. */
  title?: string;
  defaultCollapsed?: boolean;
  className?: string;
}

/**
 * How the list is headed when every row targets one resource type. A type
 * with no entry never lists: signals and automations review on a card.
 */
const LIST_HEADERS: Partial<
  Record<CopilotProposalResourceType, { title: string; Icon: Icon }>
> = {
  playbook: {
    title: "Playbook suggestions",
    Icon: resourceTypeIcons.playbook,
  },
  teammateAgent: {
    title: "Voice agent suggestions",
    Icon: resourceTypeIcons.voiceAgent,
  },
  knowledgeBaseAttachment: {
    title: "Knowledge base suggestions",
    Icon: IconFileText,
  },
};

/** The one resource type the rows share, or nothing when they mix. */
function findSharedResourceType(
  rows: readonly CopilotDeepLinkListRow[],
): CopilotProposalResourceType | undefined {
  const resourceTypes = new Set(rows.map((row) => row.resourceType));
  const [onlyResourceType] = resourceTypes;
  return resourceTypes.size === 1 ? onlyResourceType : undefined;
}

const ROW_STATUS_LABELS: Record<
  NonNullable<CopilotDeepLinkListRow["status"]>,
  string | undefined
> = {
  pending: undefined,
  applied: "Saved",
  discarded: "Outdated",
  stale: "Outdated",
};

/**
 * The rows one chat turn produced that deep-link into an editor — its
 * proposals, or its saved test results — rendered inline beneath the turn's
 * message. Collapsible so a long list doesn't crowd the transcript.
 */
export const CopilotDeepLinkList = <Id extends string>({
  rows,
  onSelect,
  activeRowId,
  title,
  defaultCollapsed = false,
  className,
}: CopilotDeepLinkListProps<Id>) => {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);

  if (rows.length === 0) {
    return null;
  }

  const sharedResourceType = findSharedResourceType(rows);
  const header = sharedResourceType
    ? LIST_HEADERS[sharedResourceType]
    : undefined;
  const ResourceIcon = header?.Icon;
  const listTitle = title ?? header?.title ?? "Suggestions";

  return (
    <div
      className={twMerge(
        "w-full min-w-0 overflow-hidden rounded-lg border border-border-light bg-white shadow-xs",
        className,
      )}
    >
      <div className="flex min-w-0 items-center gap-2 p-3">
        {ResourceIcon ? (
          <span className="flex size-7 shrink-0 items-center justify-center rounded-md bg-indigo-50 text-indigo-700">
            <ResourceIcon aria-hidden className="size-4" />
          </span>
        ) : null}
        <span className="min-w-0 flex-1 truncate text-xs font-medium text-muted-foreground">
          {listTitle}
        </span>
        <IconButton
          variant={ButtonVariant.Tertiary}
          size={Size.SMALL}
          accessibleLabel={
            isCollapsed ? `Expand ${listTitle}` : `Collapse ${listTitle}`
          }
          onClick={() => setIsCollapsed((prev) => !prev)}
          className="-mr-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          icon={
            <IconChevronDown
              aria-hidden
              className={twMerge(
                "size-4 transition-transform duration-200 ease-out",
                isCollapsed && "-rotate-90",
              )}
            />
          }
        />
      </div>
      <div
        className={twMerge(
          "grid transition-[grid-template-rows,opacity] duration-200 ease-out",
          isCollapsed
            ? "grid-rows-[0fr] opacity-0"
            : "grid-rows-[1fr] opacity-100",
        )}
      >
        <ul className="flex min-w-0 flex-col overflow-hidden border-t border-gray-100">
          {rows.map((row, index) => {
            // A decided or outdated row reads its status even while the
            // editor still shows it.
            const isLive = row.status === undefined || row.status === "pending";
            const isViewing = isLive && row.id === activeRowId;
            const opens = isLive && !isViewing;
            return (
              <li key={row.id} className="min-w-0">
                <button
                  type="button"
                  disabled={!opens}
                  onClick={() => {
                    onSelect(row);
                  }}
                  className={twMerge(
                    "group flex min-h-10 w-full items-center gap-2 px-3 py-2 text-left text-[13px] leading-[18px] focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-indigo-600",
                    opens
                      ? "text-foreground hover:bg-gray-50"
                      : isViewing
                        ? "cursor-default bg-indigo-50/60 text-foreground"
                        : "cursor-default text-muted-foreground",
                    index > 0 && "border-t border-gray-100",
                  )}
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate" title={row.name}>
                      {row.name}
                    </span>
                    {row.resourceName ? (
                      <span className="block truncate text-xs text-muted-foreground">
                        {row.resourceName}
                      </span>
                    ) : null}
                  </span>
                  {isViewing ? (
                    <span className="inline-flex shrink-0 items-center gap-1 text-xs text-indigo-700">
                      <IconEye aria-hidden className="size-3.5" />
                      Viewing
                    </span>
                  ) : opens ? (
                    <IconArrowRight
                      aria-hidden
                      className="size-4 shrink-0 text-gray-400 transition-transform duration-150 group-hover:translate-x-0.5 group-hover:text-gray-600"
                    />
                  ) : (
                    <span className="shrink-0 text-xs text-muted-foreground">
                      {row.status && ROW_STATUS_LABELS[row.status]}
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};
